import { useState, useRef, useCallback } from 'react';
import { 
  Box,
  Stack,
  Typography,
  CircularProgress
} from '@mui/joy';
import { styled } from '@mui/joy';

const DropZone = styled(Box)(({ theme, isDragging, isProcessing }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary[500] : theme.palette.neutral[300]}`,
  borderRadius: theme.radius.md,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: isProcessing ? 'not-allowed' : 'pointer',
  backgroundColor: isDragging ? theme.palette.primary[50] : theme.palette.background.surface,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  '&:hover': {
    borderColor: isProcessing ? undefined : theme.palette.primary[400],
    backgroundColor: isProcessing ? undefined : theme.palette.primary[50],
  },
}));

const ImagePreview = styled('img')({
  maxWidth: '100%',
  maxHeight: '300px',
  borderRadius: '8px',
  objectFit: 'contain',
});

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export function DragDropFileUpload({
  onFileSelect,
  isLoading = false,
  accept = 'image/*',
  selectedFile = null,
  previewUrl = null,
  dragText = 'Drag and drop an image here',
  dropText = 'Drop image here',
  clickText = 'or click to browse',
  processingText = null,
  showPreview = true,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (file && onFileSelect) {
      await onFileSelect(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFileSelect]);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file && onFileSelect) {
      await onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    if (!isLoading) {
      setIsDragging(true);
    }
  }, [isLoading]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    const dropZone = event.currentTarget;
    const relatedTarget = event.relatedTarget;
    
    if (!relatedTarget || !dropZone.contains(relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleClick = useCallback(() => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isLoading]);

  const displayProcessingText = processingText || (selectedFile ? `Processing ${selectedFile.name}...` : 'Processing...');

  return (
    <DropZone
      isDragging={isDragging}
      isProcessing={isLoading}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      <VisuallyHiddenInput
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept={accept}
        disabled={isLoading}
      />
      
      {isLoading && selectedFile ? (
        <Stack spacing={2} alignItems="center">
          <CircularProgress size="lg" />
          <Typography level="body-md">
            {displayProcessingText}
          </Typography>
          {showPreview && previewUrl && (
            <ImagePreview src={previewUrl} alt="Preview" />
          )}
        </Stack>
      ) : previewUrl && selectedFile && showPreview ? (
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <ImagePreview src={previewUrl} alt="Preview" />
          <Typography level="body-sm" color="neutral">
            {selectedFile.name}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2} alignItems="center">
          <Box
            component="svg"
            sx={{
              width: 64,
              height: 64,
              color: 'neutral.400',
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
            />
          </Box>
          <Stack spacing={0.5} alignItems="center">
            <Typography level="body-md">
              {isDragging ? dropText : dragText}
            </Typography>
            <Typography level="body-sm" color="neutral">
              {clickText}
            </Typography>
          </Stack>
        </Stack>
      )}
    </DropZone>
  );
}

